<template>
  <div id="app" class="container is-widescreen">
    <ncl-header :showDropdowns="shouldShowDropdowns" />
    <div :class="{ 'text-page': isTextPage }">
      <router-view></router-view>
    </div>
    <ncl-footer />
  </div>
</template>

<script>
import NclHeader from "@/components/NclHeader.vue";
import NclFooter from "@/components/NclFooter.vue";
import { mapStores } from "pinia";
import { useCountryStore } from "@/stores/country";
import { useTheme } from "vuetify";
import { watch } from "vue";

export default {
  name: "app",
  components: { NclHeader, NclFooter },
  setup() {
    const theme = useTheme();

    // Watch for theme changes and apply to body + <html data-theme>
    // (Bulma 1.0 uses data-theme / prefers-color-scheme to switch dark mode,
    // so we must pin it explicitly or it desyncs from the app theme.)
    watch(
      () => theme.global.name.value,
      (newTheme) => {
        const isDark = newTheme === "nclDarkTheme";
        document.body.classList.toggle("dark-theme", isDark);
        document.body.classList.toggle("light-theme", !isDark);
        document.documentElement.dataset.theme = isDark ? "dark" : "light";
      },
      { immediate: true },
    );

    return {};
  },
  computed: {
    ...mapStores(useCountryStore),
    shouldShowDropdowns() {
      // Show dropdowns on Overview (Home) and Detail pages
      return this.$route.name === "Home" || this.$route.name === "Detail";
    },
    isTextPage() {
      return this.$route.name !== "Home" && this.$route.name !== "Detail";
    },
  },
  async created() {
    // Load quarters once at app level before any pages initialize
    // This ensures quarters are available for all pages
    if (this.countryStore.quarters.length === 0) {
      await this.countryStore.loadQuarters();
    }
  },
};
</script>

<style lang="scss">
// Import global CSS Variables (Theme)
@use "./styles/variables.scss" as *;

@forward "./styles/base.scss";
@forward "./styles/layout.scss";
@forward "./styles/utilities.scss";

#app {
  color: $color-text;
  font-family: "IBM Plex Sans";
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}

.text-page {
  padding: 1rem 1.5rem;

  @media (min-width: 769px) {
    padding: 1.5rem 2rem;
  }
}

// Dark theme styles
body.dark-theme {
  background-color: #1e1e1e;
  color: #e0e0e0;

  #app {
    background-color: #1e1e1e;
    color: #e0e0e0;
  }

  .box {
    background-color: #2d2d2d;
    color: #e0e0e0;
  }

  .title,
  .subtitle,
  .heading {
    color: #e0e0e0;
  }

  .navbar {
    background-color: #2d2d2d;

    .navbar-item,
    .navbar-link {
      color: #e0e0e0;
    }

    .navbar-item:hover {
      background-color: #404043;
      color: rgb(124, 181, 236);
    }

    .navbar-item a:hover {
      color: rgb(124, 181, 236);
    }

    .navbar-item.router-link-active,
    .navbar-item.router-link-exact-active {
      background-color: #404043;
      color: rgb(124, 181, 236);
    }

    .navbar-dropdown {
      background-color: #2d2d2d;
      border-color: #404043;
    }

    .navbar-item.dropdown-option:hover {
      background-color: #404043;
    }
  }

  .navbar-row-1 {
    border-bottom-color: #404043;
  }
}

// Light theme styles
body.light-theme {
  background-color: #ffffff;
  color: #333333;

  #app {
    background-color: #ffffff;
    color: #333333;
  }
}

// Navbar is position: sticky (see NclHeader.vue) — content flows below it
// naturally, no hardcoded offsets needed.
</style>
